import type {
  BillingActivationFailure,
  SuperBillingRow
} from '@tandemone/types'
import { isCompedSubscription, PLAN_CATALOG } from '@tandemone/types'
import type { ColumnDef } from '@tanstack/react-table'
import { Download } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { extractErrorMessage } from '@/api/extract-error'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header'
import { cn } from '@/lib/utils'

import { getSuperInvoiceLinks } from './api'
import { formatBillingDate } from './plan-price'
import { goToStripe, openStripeTab } from './stripe-tab'

const ACTIVATION_FAILURE_LABELS: Record<BillingActivationFailure, string> = {
  no_payment_method: 'No card on file',
  card_declined: 'Card declined',
  stripe_error: 'Stripe error'
}

/* anything not simply active is what support is here to look at. */
export function SuperBillingStatusBadge({ row }: { row: SuperBillingRow }) {
  /* checked before status: a failed activation leaves the company on free and
   * active, which would otherwise read as nothing to look at. */
  if (row.activationFailure) {
    return (
      <Badge className='border-transparent bg-red-100 text-red-900'>
        {ACTIVATION_FAILURE_LABELS[row.activationFailure]}
      </Badge>
    )
  }
  if (row.status === 'active') return <Badge variant='outline'>Active</Badge>
  if (row.status === 'past_due') {
    return (
      <Badge className='border-transparent bg-amber-100 text-amber-900'>
        Past due
      </Badge>
    )
  }
  if (row.status === 'pending_approval') {
    return (
      <Badge className='border-transparent bg-sky-100 text-sky-900'>
        Pending approval
      </Badge>
    )
  }
  if (row.status === 'incomplete') {
    return (
      <Badge className='border-transparent bg-amber-100 text-amber-900'>
        Awaiting payment
      </Badge>
    )
  }
  return <Badge variant='outline'>{row.status}</Badge>
}

/* fetched on click, not stored: stripe expires a hosted invoice url 30 days
 * after its due date and rotates the pdf link. */
export function ReceiptLink({ row }: { row: SuperBillingRow }) {
  const [busy, setBusy] = useState(false)
  const invoiceId = row.lastInvoiceId

  if (!invoiceId) return <span className='text-muted-foreground'>—</span>

  async function open(tab: Window | null) {
    if (!invoiceId) return
    setBusy(true)
    try {
      const links = await getSuperInvoiceLinks(row.companyId, invoiceId)
      const url = links.invoicePdfUrl ?? links.hostedInvoiceUrl
      if (!url) {
        tab?.close()
        toast.error('That invoice is no longer available from Stripe')
        return
      }
      goToStripe(url, tab)
    } catch (error) {
      tab?.close()
      toast.error(extractErrorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      size='sm'
      disabled={busy}
      onClick={() => void open(openStripeTab())}
    >
      <Download className='size-3.5' />
      Download
    </Button>
  )
}

export const superBillingColumns: ColumnDef<SuperBillingRow>[] = [
  {
    // id is the wire sort field; the value lives on companyName.
    id: 'companyName',
    meta: { width: '28%' },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Company' />
    ),
    cell: ({ row }) => (
      <span className='block truncate font-medium'>
        {row.original.companyName}
      </span>
    )
  },
  {
    accessorKey: 'planId',
    meta: { width: '18%' },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Plan' />
    ),
    /* null when the company has never had a subscription: listed so it can be
       comped, so it needs a plan cell that says "nothing yet". */
    cell: ({ row }) => (
      <span
        className={cn(
          'block truncate',
          row.original.planId === null && 'text-ink-muted'
        )}
      >
        {row.original.planId
          ? PLAN_CATALOG[row.original.planId].label
          : 'No plan'}
      </span>
    )
  },
  {
    id: 'billingInterval',
    meta: { width: '110px' },
    header: 'Billing',
    enableSorting: false,
    /* null on comped grants and free plans: nothing is being billed. */
    cell: ({ row }) =>
      row.original.billingInterval === null
        ? '—'
        : row.original.billingInterval === 'year'
          ? 'Annually'
          : 'Monthly'
  },
  {
    accessorKey: 'status',
    meta: { width: '150px' },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Status' />
    ),
    cell: ({ row }) => (
      <div className='flex flex-wrap items-center gap-1.5'>
        <SuperBillingStatusBadge row={row.original} />
        {isCompedSubscription(row.original) && (
          <Badge variant='outline'>Comped</Badge>
        )}
      </div>
    )
  },
  {
    accessorKey: 'lastPaidAt',
    meta: { width: '130px' },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Date paid' />
    ),
    cell: ({ row }) => formatBillingDate(row.original.lastPaidAt)
  },
  {
    id: 'receipt',
    header: 'Receipt',
    meta: { width: '130px', excludeFromRowLink: true },
    enableSorting: false,
    cell: ({ row }) => <ReceiptLink row={row.original} />
  }
]
